var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth';
import { ACCORDION_TRANSITION_DURATION, AlertIDType } from '../../../const/alertsConfig';
import { CLICK_TRACKING_EVENT, DATA_OBJECT } from '../../../const/analytics';
import mediaQueries from '../../../mediaQueries';
import { helpers } from '../../../helpers';
import { alertsFormActions } from '../../../redux/alertsForm/actions';
import { getAlertFormDataByID, getAlertsDataByID, getPurchaseExceedsDataErrors } from '../../../redux/selectors';
import { AlertAttributeTypes } from '../../../typings/enums';
import { useLockScreen } from '../../customHooks';
import Flipper from '../../templates/Flipper';
import AlertBody from '../AlertBody';
import AlertHeader from '../AlertHeader';
import TermsConditions from '../TermsConditions';
import { AccordionTermsAndConditionsWrapper, AccordionWrapper, AlertsAccordion } from './subcomponents';
// The UI should break to mobile design when is <= 768.
var MOBILE_SIZE = parseFloat(mediaQueries.medium);
var inputErrors = [
    AlertAttributeTypes.BalanceExceedsThresholdAmount,
    AlertAttributeTypes.AvailableCreditThresholdAmount,
    AlertAttributeTypes.GenerousTipPercentage
];
var AlertItem = function (_a) {
    var _b, _c;
    var alertID = _a.alertID;
    var dispatch = useDispatch();
    var alertFormData = useSelector(getAlertFormDataByID(alertID), shallowEqual);
    var dataErrorsForPurchaseExceedsAlert = useSelector(getPurchaseExceedsDataErrors, shallowEqual);
    // The below code represents a real source of complexity and pain
    var isApprovedTransactions = alertID === AlertIDType.APPROVED_TRANSACTIONS;
    var hasPurchaseExceedsValue = ((_c = (_b = alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.attributes) === null || _b === void 0 ? void 0 : _b.approvedTransactionType) === null || _c === void 0 ? void 0 : _c.value) ===
        AlertIDType.PURCHASE_EXCEEDS;
    var dataErrors = alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.dataErrors;
    var _d = useState(false), saveOrTurnOnClicked = _d[0], setSaveOrTurnOnClicked = _d[1];
    var inputRef = useRef(null);
    var deliveryRef = useRef(null);
    var updateInfoLinkRef = useRef(null);
    var currentAlertData = useSelector(getAlertsDataByID(alertID), shallowEqual);
    var dataIsLoaded = !!(currentAlertData && alertFormData);
    var shouldShowTermsAndConditionsComponent = dataIsLoaded && alertFormData.showTermsAndConditions;
    var openAlertsFormAccordion = alertsFormActions.openAlertsFormAccordion, closeAccordionAndResetData = alertsFormActions.closeAccordionAndResetData;
    var setFocus = helpers.setFocus;
    // This is the call back function used by the accordion when expanded/collapsed
    var onAccordionHeaderToggle = useCallback(function (isExpanded) {
        if (isExpanded) {
            dispatch(openAlertsFormAccordion(alertID));
        }
        else {
            dispatch(closeAccordionAndResetData(alertID));
        }
    }, [alertID, closeAccordionAndResetData, dispatch, openAlertsFormAccordion]);
    // Locking Screen Logic
    var accordionWrapperRef = useRef(null);
    var _e = useState(false), isMobile = _e[0], setIsMobile = _e[1];
    // useWindowSize custom hook from Component Library
    var windowInfo = useWindowSize();
    useEffect(function () {
        var currentWidth = windowInfo.width;
        setIsMobile(currentWidth <= MOBILE_SIZE);
    }, [windowInfo]);
    var shouldLock = shouldShowTermsAndConditionsComponent && isMobile;
    useLockScreen(shouldLock, accordionWrapperRef);
    // END Locking Screen Logic
    var accordionId = "accordion_".concat(alertID);
    // focus order on errors
    var handleSaveOrTurnOnClick = function () {
        var _a;
        var element = (_a = document.getElementById(accordionId)) === null || _a === void 0 ? void 0 : _a.firstChild;
        element === null || element === void 0 ? void 0 : element.focus();
        setSaveOrTurnOnClicked(true);
    };
    if (saveOrTurnOnClicked) {
        if (
        // This ternary would be unnecessary if we didn't lose the actual value of alertId
        isApprovedTransactions && hasPurchaseExceedsValue
            ? dataErrorsForPurchaseExceedsAlert.some(function (error) {
                return error.key === AlertAttributeTypes.PurchaseExceedsThresholdAmount;
            })
            : dataErrors.some(function (error) {
                return inputErrors.includes(error.key);
            })) {
            setFocus(inputRef);
        }
        else if (dataErrors.some(function (error) { return error.key === 'delivery'; })) {
            setFocus(deliveryRef);
        }
        setSaveOrTurnOnClicked(false);
    }
    // end : focus order on errors
    // Start-focus on t&c cancel
    var buttonRef = useRef(null);
    var setFocusOnCancel = function () {
        setFocus(buttonRef);
    };
    // End-focus on t&c cancel
    return (_jsx(AccordionWrapper, __assign({ "data-test": "accordion-".concat(alertID), ref: accordionWrapperRef, shouldShowTermsAndConditionsComponent: shouldShowTermsAndConditionsComponent, "data-object": DATA_OBJECT.ACCORDION, "data-analytics": CLICK_TRACKING_EVENT }, { children: _jsx(Flipper, { shouldFlipContent: shouldShowTermsAndConditionsComponent, frontContent: _jsx(AlertsAccordion, { id: accordionId, "data-test": "Accordion", forceRerenderBody: true, isExpanded: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.isExpanded, header: dataIsLoaded && (_jsx(AlertHeader, { "data-test": "AlertHeader", currentAlertData: currentAlertData, isAccordionExpanded: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.isExpanded, showDeliveryIcons: !shouldShowTermsAndConditionsComponent })), body: dataIsLoaded && (_jsx(AlertBody, { "data-test": "AlertBody", currentAlertData: currentAlertData, alertFormData: alertFormData, focusRef: buttonRef, inputRef: inputRef, deliveryRef: deliveryRef, onSaveOrTurnOnButtonClick: handleSaveOrTurnOnClick })), transitionDuration: ACCORDION_TRANSITION_DURATION, headerClickCallBack: onAccordionHeaderToggle, forceClose: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.forceClose }), backContent: _jsx(AlertsAccordion, { id: "accordion_".concat(alertID), "data-test": "accordion-".concat(alertID), forceRerenderBody: true, isExpanded: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.isExpanded, header: _jsx(AlertHeader, { "data-test": "AlertHeader", currentAlertData: currentAlertData, isAccordionExpanded: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.isExpanded, showDeliveryIcons: !shouldShowTermsAndConditionsComponent }), body: _jsx(AccordionTermsAndConditionsWrapper, __assign({ "data-test": "AccordionTermsConditionsWrapper" }, { children: _jsx(TermsConditions, { "data-test": "TermsConditions", currentAlertData: currentAlertData, alertFormData: alertFormData, onCancel: setFocusOnCancel, isProfileDataVisible: true }) })), transitionDuration: ACCORDION_TRANSITION_DURATION, headerClickCallBack: onAccordionHeaderToggle, forceClose: alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.forceClose }), focusCustomElement: updateInfoLinkRef }) })));
};
export default AlertItem;
